/* eslint-disable max-params */
import React from "react";
import { notification } from "antd";
import dayjs from "dayjs";
import { getAccessJwtToken } from "api/base";
import { AxiosError } from "axios";
import {
  LOCAL_STORAGE_KEY,
  NOTIFICATION_TYPE,
  SYSTEM_CONSTANT,
  USER_GROUP,
  USER_ROLE,
  SPECIAL_ORG_CODES,
  USER_ROLE_KOJI,
  WORK_START_HOUR,
  WORK_END_HOUR,
  EVENING_START_OFFSET_MINUTES,
  FORMAT_TIME
} from "constants/common.constant";
import { ROUTERS } from "constants/routes.constant";
import { jwtDecode } from "jwt-decode";
import { TypeCommon } from "models/common";
import moment from "moment";
import Resizer from "react-image-file-resizer";

import duration from 'dayjs/plugin/duration';
import isBetween from 'dayjs/plugin/isBetween';
import utc from 'dayjs/plugin/utc';
import minMax from 'dayjs/plugin/minMax';

dayjs.extend(duration);
dayjs.extend(isBetween);
dayjs.extend(utc);
dayjs.extend(minMax);

export const isJsonObject = (strData: TypeCommon): boolean => {
  try {
    JSON.parse(strData);
  } catch (e) {
    return false;
  }
  return true;
};

export const ToastMessage = (
  type: string,
  messTitle: string | React.ReactNode,
  description = "",
): void => {
  let typeToast = notification.info;
  switch (type) {
    case NOTIFICATION_TYPE.INFO:
      typeToast = notification.info;
      break;
    case NOTIFICATION_TYPE.ERROR:
      typeToast = notification.error;
      break;
    case NOTIFICATION_TYPE.SUCCESS:
      typeToast = notification.success;
      break;
    case NOTIFICATION_TYPE.WARNING:
      typeToast = notification.warning;
      break;
    default:
      break;
  }
  return typeToast({
    message: isJsonObject(messTitle) ? JSON.stringify(messTitle) : messTitle,
    description,
    duration: 3,
  });
};

export const handleError = (
  error: AxiosError<TypeCommon>,
  rejectWithValue: (value: unknown) => TypeCommon,
): TypeCommon => {
  const err: AxiosError<TypeCommon> = error;
  if (!err.response) {
    throw err;
  }
  return rejectWithValue(err.response.data);
};

export const formatDate = (date: TypeCommon, format?: string): string => {
  if (!date || !moment(date).isValid()) {
    return "";
  }
  return moment(new Date(date)).format(
    format ?? SYSTEM_CONSTANT.SYSTEM_LOCAL_DATE_FORMAT,
  );
};

export const addSuffixtoString = (): TypeCommon => {
  const date = new Date();
  return (
    date.getFullYear() +
    ("0" + (date.getMonth() + 1)).slice(-2) +
    ("0" + date.getDate()).slice(-2) +
    ("0" + date.getHours()).slice(-2) +
    ("0" + date.getMinutes()).slice(-2) +
    ("0" + date.getSeconds()).slice(-2)
  );
};

export const checkToken = async (): Promise<void> => {
  const token = await getAccessJwtToken();
  if (token) {
    const { exp } = jwtDecode(token) as TypeCommon;
    const expirationTime = exp * 1000 - 60000;
    if (Date.now() >= expirationTime) {
      removeLocalDataIgnoreKey([LOCAL_STORAGE_KEY.LIST_USER]);
      sessionStorage.clear();
      window.location.assign(ROUTERS.LOGIN);
    }
  } else if (!token && window.location.pathname !== ROUTERS.LOGIN) {
    removeLocalDataIgnoreKey([LOCAL_STORAGE_KEY.LIST_USER]);
    sessionStorage.clear();
    window.location.assign(ROUTERS.LOGIN);
  }
};

export const resizeFile = (
  file: TypeCommon,
  width?: number,
  height?: number,
): TypeCommon =>
  new Promise((resolve) => {
    Resizer.imageFileResizer(
      file,
      width || 1000,
      height || 1000,
      "JPEG",
      100,
      0,
      (uri) => {
        resolve(uri);
      },
      "file",
    );
  });

export const generateUserRole = (user: TypeCommon): string => {
  let role = "";
  switch (user?.group) {
    case USER_GROUP.PLATFORM_ADMIN: {
      if (user?.role === USER_ROLE.ROOT) role = "プラットフォームルート";
      else if (user?.role === USER_ROLE.MANAGER)
        role = "プラットフォーム管理者";
      else role = "プラットフォーム運用者";
      break;
    }
    case USER_GROUP.ADMIN: {
      if (user?.role === USER_ROLE.ROOT) role = "サービスルート";
      else if (user?.role === USER_ROLE.MANAGER) role = "サービス管理者";
      else role = "サービス運用者";
      break;
    }
    case USER_GROUP.APPROVER: {
      if (user?.role === USER_ROLE.ROOT) role = "承認ルート";
      else if (user?.role === USER_ROLE.MANAGER) role = "承認管理者";
      else role = "一般承認者";
      break;
    }
    default: {
      if (user?.role === USER_ROLE.ROOT) role = "申請ルート";
      else if (user?.role === USER_ROLE.MANAGER) role = "申請管理者";
      else role = "一般申請者";
      break;
    }
  }
  return role;
};

export const removeLocalDataIgnoreKey = (keepKeys: string[]): void => {
  Object.keys(localStorage).forEach((key) => {
    if (!keepKeys?.includes(key)) {
      localStorage.removeItem(key);
    }
  });
};

export const getPriorityColor = (priority: TypeCommon): string => {
  switch (priority) {
    case 1:
      return "error.main";
    case 2:
      return "warning.main";
    case 3:
      return "success.main";
    default:
      return "text.secondary";
  }
};

export const getPriorityText = (priority: TypeCommon): string => {
  switch (priority) {
    case 1:
      return "高";
    case 2:
      return "中";
    case 3:
      return "低";
    default:
      return "";
  }
};

export const getStatusText = (status: TypeCommon): string => {
  switch (status) {
    case "SUBMITTING":
      return "申請中";
    case "WAITING_FOR_APPROVAL":
      return "承認待ち";
    case "IN_PROGRESS":
      return "進行中";
    case "COMPLETED":
      return "完了";
    case "ON_HOLD":
      return "保留";
    case "CANCELLED":
      return "中止";
    default:
      return "";
  }
};

export const getStatusTaskText = (status: TypeCommon): TypeCommon => {
  switch (status) {
    case "APPLYING":
      return {
        text: "未確認",
        color: '#616161'
      };
    case "WAITING_FOR_APPROVAL":
      return {
        text: "未確認",
        color: '#616161'
      };
    case "REJECT":
      return {
        text: "差し戻し",
        color: '#D32F2F'
      };
    case "PENDING":
      return {
        text: "完了",
        color: '#00A651'
      };
    case "DONE":
      return {
        text: "完了",
        color: '#00A651'
      };
    case "REJECTED":
      return {
        text: "差し戻し",
        color: '#D32F2F'
      };
    case "APPROVED":
      return {
        text: "完了",
        color: '#00A651'
      };
    case "WAITING_FOR_REVIEW":
      return {
        text: "完了",
        color: '#00A651'
      };
    default:
      return "";
  }
}

export const getStatusColor = (status: TypeCommon): string => {
  switch (status) {
    case "SUBMITTING":
      return "#1976D2";
    case "WAITING_FOR_APPROVAL":
      return "#F57C00";
    case "IN_PROGRESS":
      return "#388E3C";
    case "COMPLETED":
      return "#0D47A1";
    case "ON_HOLD":
      return "#616161";
    case "CANCELLED":
      return "#D32F2F";
    default:
      return "";
  }
};

export const getProcessStatusText = (status: TypeCommon): string => {
  switch (status) {
    case "REQUEST":
      return "依頼";
    case "ESTIMATE":
      return "見積";
    case "PLAN":
      return "企画";
    case "SCHEDULE_PROGRESS":
      return "警備員手配";
    case "DATE_CONFIRMED":
      return "日程調整";
    case "COMPLETED":
      return "工事完了";
    default:
      return "";
  }
};

export const getTaskStatusText = (status: TypeCommon): string => {
  switch (status) {
    case "APPLYING":
      return "申請中";
    case "WAITING_FOR_APPROVAL":
      return "承認待ち";
    case "IN_PROGRESS":
      return "進行中";
    case "DONE":
      return "完了";
    case "PENDING":
      return "保留";
    case "CANCELLED":
      return "中止";
    default:
      return "";
  }
};

export const getConfigTaskStatusText = (status: TypeCommon): TypeCommon => {
  switch (status) {
    case "APPLYING":
      return {
        text: "未対応",
        color: '#616161'
      };
    case "WAITING_FOR_APPROVAL":
      return {
        text: "対応中",
        color: '#F57C00'
      };
    case "IN_PROGRESS":
      return {
        text: "進行中",
        color: '#3B82F6'
      };
    case "DONE":
      return {
        text: "完了",
        color: '#00A651'
      };
    case "PENDING":
      return {
        text: "完了",
        color: '#00A651'
      };
    case "CANCELLED":
      return {
        text: "中止",
        color: '#D32F2F'
      };
    case "REJECT":
      return {
        text: "差し戻し",
        color: '#D32F2F'
      };
    case "WAITING_FOR_REVIEW":
      return {
        text: "完了",
        color: '#00A651'
      };
    case "WAITING_CREATE":
      return {
        text: "未対応",
        color: '#616161'
      };
    default:
      return {
        text: "未対応",
        color: '#616161'
      };;
  }
};

/**
 * Validates if the time range between start and end dates is within the specified limit
 * @param startDate - The start date (dayjs object)
 * @param endDate - The end date (dayjs object)
 * @param maxMonths - Maximum number of months allowed (default: 3)
 * @returns Object with isValid boolean and error message if invalid
 */
export const validateDateRange = (
  startDate: TypeCommon,
  endDate: TypeCommon,
  maxMonths: number = 3,
  isIgnoreMaxMonths?: boolean,
): { isValid: boolean; errorMessage?: string } => {
  // Check if both dates exist
  if (!startDate || !endDate) {
    return { isValid: true };
  }

  // Check if start date is before end date
  if (startDate.isAfter(endDate)) {
    return {
      isValid: false,
      errorMessage: "終了日は開始日より後である必要があります"
    };
  }

  // Check if the time range is more than maxMonths
  const maxDate = startDate.add(maxMonths, 'month');
  if (endDate.isAfter(maxDate) && !isIgnoreMaxMonths) {
    return {
      isValid: false,
      errorMessage: `開始日から終了日までの期間は${maxMonths}ヶ月以内にしてください`
    };
  }

  return { isValid: true };
};

/**
 * Validates if a date range overlaps with any other date ranges in the array
 * @param currentIndex - The index of the current date range being validated
 * @param dateRanges - Array of all date ranges
 * @returns Object with isValid boolean and error message if invalid
 */
export const validateOverlappingDateRanges = (
  currentIndex: number,
  dateRanges: TypeCommon[]
): { isValid: boolean; errorMessage?: string } => {
  const currentRange = dateRanges[currentIndex];
  
  // If current range has no dates, skip validation
  if (!currentRange?.startDate && !currentRange?.endDate) {
    return { isValid: true };
  }

  // Check for overlaps with other ranges
  for (let i = 0; i < dateRanges.length; i+=1) {
    if (i === currentIndex) continue; // Skip self
    
    const otherRange = dateRanges[i];
    
    // Skip ranges that have no dates at all
    if (!otherRange?.startDate && !otherRange?.endDate) continue;
    
    // Check if ranges overlap
    // Two ranges overlap if: start1 <= end2 AND start2 <= end1
    // This includes cases where start dates are the same
    
    // If current range has both dates and other range has both dates
    if (currentRange?.startDate && currentRange?.endDate && otherRange?.startDate && otherRange?.endDate) {
      // Cho phép trường hợp start của ngày này bằng end của ngày trước đó
      // So sánh đến phút, bỏ qua giây và milliseconds
      const isStartEqualsEnd = currentRange.startDate.format('YYYY-MM-DD HH:mm') === otherRange.endDate.format('YYYY-MM-DD HH:mm');
      const isEndEqualsStart = currentRange.endDate.format('YYYY-MM-DD HH:mm') === otherRange.startDate.format('YYYY-MM-DD HH:mm');
      
      // Nếu start của ngày này bằng end của ngày trước đó, hoặc end của ngày này bằng start của ngày trước đó, thì không overlap
      if (isStartEqualsEnd || isEndEqualsStart) {
        continue;
      }
      
      const hasOverlap = 
        (currentRange.startDate.isBefore(otherRange.endDate)) && 
        (otherRange.startDate.isBefore(currentRange.endDate));
      
      if (hasOverlap) {
        return {
          isValid: false,
          errorMessage: "作業日時が重複にならない様に指定してください"
        };
      }
    }
    
    // If current range has only startDate, check if it conflicts with other ranges
    if (currentRange?.startDate && !currentRange?.endDate) {
      // So sánh đến phút, bỏ qua giây và milliseconds
      if (otherRange?.startDate && currentRange.startDate.format('YYYY-MM-DD HH:mm') === otherRange.startDate.format('YYYY-MM-DD HH:mm')) {
        return {
          isValid: false,
          errorMessage: "作業日時が重複にならない様に指定してください"
        };
      }
      // Cho phép trường hợp start của ngày này bằng end của ngày trước đó
      if (otherRange?.endDate && currentRange.startDate.format('YYYY-MM-DD HH:mm') === otherRange.endDate.format('YYYY-MM-DD HH:mm')) {
        continue; // Không báo lỗi trong trường hợp này
      }
      // Kiểm tra nếu startDate nằm trong khoảng thời gian của ngày khác
      if (otherRange?.startDate && otherRange?.endDate) {
        const isInRange = currentRange.startDate.isAfter(otherRange.startDate) && currentRange.startDate.isBefore(otherRange.endDate);
        if (isInRange) {
          return {
            isValid: false,
            errorMessage: "作業日時が重複にならない様に指定してください"
          };
        }
      }
    }
    
    // If current range has only endDate, check if it conflicts with other ranges
    if (currentRange?.endDate && !currentRange?.startDate) {
      // So sánh đến phút, bỏ qua giây và milliseconds
      if (otherRange?.endDate && currentRange.endDate.format('YYYY-MM-DD HH:mm') === otherRange.endDate.format('YYYY-MM-DD HH:mm')) {
        return {
          isValid: false,
          errorMessage: "作業日時が重複にならない様に指定してください"
        };
      }
      // Cho phép trường hợp end của ngày này bằng start của ngày trước đó
      if (otherRange?.startDate && currentRange.endDate.format('YYYY-MM-DD HH:mm') === otherRange.startDate.format('YYYY-MM-DD HH:mm')) {
        continue; // Không báo lỗi trong trường hợp này
      }
      // Kiểm tra nếu endDate nằm trong khoảng thời gian của ngày khác
      if (otherRange?.startDate && otherRange?.endDate) {
        const isInRange = currentRange.endDate.isAfter(otherRange.startDate) && currentRange.endDate.isBefore(otherRange.endDate);
        if (isInRange) {
          return {
            isValid: false,
            errorMessage: "作業日時が重複にならない様に指定してください"
          };
        }
      }
    }
  }

  return { isValid: true };
};

/**
 * Format date safely with dayjs to avoid null errors
 * @param date - The date to format (can be dayjs object, string, or null/undefined)
 * @param format - The format string (default: "YYYY-MM-DD HH:mm:ss")
 * @returns Formatted date string or empty string if date is invalid/null
 */
export const formatDateSafely = (date: TypeCommon, format: string = "YYYY-MM-DD HH:mm:ss"): string => {
  if (!date) {
    return "";
  }
  
  try {
    // If date is already a dayjs object
    if (date && typeof date.format === 'function') {
      return date.isValid() ? date.format(format) : "";
    }
    
    // If date is a string or other format, try to parse with dayjs
    const parsedDate = dayjs(date);
    return parsedDate.isValid() ? parsedDate.format(format) : "";
  } catch (error) {
    return "";
  }
};

/**
 * Format date range safely for API submission
 * @param startDate - Start date (can be dayjs object, string, or null/undefined)
 * @param endDate - End date (can be dayjs object, string, or null/undefined)
 * @param format - The format string (default: "YYYY-MM-DD HH:mm:ss")
 * @returns Object with formatted start_date and end_date
 */
export const formatDateRangeSafely = (
  startDate: TypeCommon, 
  endDate: TypeCommon, 
  format: string = "YYYY-MM-DD HH:mm:ss"
): { start_date: string; end_date: string } => {
  return {
    start_date: formatDateSafely(startDate, format),
    end_date: formatDateSafely(endDate, format)
  };
};

export const normalizeFullwidthToHalfwidth = (str: string): string => {
  return str.replace(/[\uFF10-\uFF19]/g, (ch) =>
    String.fromCharCode(ch.charCodeAt(0) - 0xFF10 + 0x30)
  );
};

export const formatNumber = (value: number | string | null | undefined): string => {
  if (!value) return "";
  const numValue = typeof value === 'string' ? parseFloat(value.replace(/,/g, '')) : value;
  if (isNaN(numValue)) return "";
  return numValue.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
};

export const isFullWidthDigit = (char: string): boolean => {
  return /[０-９]/.test(char);
}

// Helper function to format number with commas
export const formatNumberWithCommas = (value: number | string | null | undefined): string => {
    if (!value) return "";
    const numValue = typeof value === 'string' ? parseFloat(value.replace(/,/g, '')) : value;
    if (isNaN(numValue)) return "";
    return numValue.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  };

export const checkUserRoleKoji = (userInfo: TypeCommon): string => {
  if (!userInfo?.role) {
    return '';
  }
  if (userInfo?.group === USER_GROUP.PLATFORM_ADMIN) {
    return USER_ROLE_KOJI.PLATFORM_ADMIN;
  }
  if (userInfo?.owner_services?.some((owner_services: TypeCommon) => owner_services?.type === "dashboard")) {
    return USER_ROLE_KOJI.APPROVER_HONBU_SERVICE_OWNER;
  }
  if (userInfo?.organization) {
    const orgCode = parseInt(userInfo?.organization?.code);
    if ("020" === userInfo?.organization?.code) {
      return USER_ROLE_KOJI.APPROVER_HONBU_020;
    }
    if (userInfo?.organization?.is_summit && orgCode > 100) {
      return USER_ROLE_KOJI.APPROVER_TENPO;
    }
    if (userInfo?.organization?.is_summit && orgCode <= 100) {
      return USER_ROLE_KOJI.APPROVER_HONBU;
    }
  }

  if (!userInfo?.organization?.is_summit) {
    if (SPECIAL_ORG_CODES.includes(userInfo?.organization?.code)) {
      return USER_ROLE_KOJI.CREATER_PARENT;
    }
    return USER_ROLE_KOJI.CREATER;
  }
  return '';
};
export const fetchTokens = async (code: string): Promise<boolean> => {
  try {
    const body = new URLSearchParams({
      grant_type: "authorization_code",
      client_id: "1tsjg0tcuoabt0u9jdk96lqh8j",
      code,
      redirect_uri: "https://dev.summitgco.com/service",
    });

    const response = await fetch(
      "https://ap-northeast-15szeo5ub9.auth.ap-northeast-1.amazoncognito.com/oauth2/token",
      {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: body.toString(),
      },
    );

    const data = await response.json();
    // eslint-disable-next-line no-console
    // console.log("data", data);

    if (data.id_token) {
      const decoded: TypeCommon = jwtDecode(data.id_token);

      // 2. Lưu thông tin vào localStorage
      localStorage.setItem("accessToken", data.access_token);
      localStorage.setItem("idToken", data.id_token);
      localStorage.setItem("refreshToken", data.refresh_token);
      localStorage.setItem("user", JSON.stringify(decoded));
      localStorage.setItem("loginProvider", "AzureAD");
      return true;
    }
    return false;
  } catch (err) {
    // eslint-disable-next-line no-console
    // console.error("Error exchanging code for tokens", err);
    return false;
  }
};

interface TimeRange {
  start_date: string; // ISO string
  end_date: string;
}

export const generateTimeSlots = (workStart: string, workEnd: string, openStr: string = WORK_START_HOUR, closeStr: string = WORK_END_HOUR): TimeRange[] => {
  if (!workStart || !workEnd) {
    return [];
  }

  const workStartDt = dayjs(workStart);
  const workEndDt = dayjs(workEnd);
  
  // Validate that the dates are valid
  if (!workStartDt.isValid() || !workEndDt.isValid()) {
    return [];
  }
  
  openStr = openStr || WORK_START_HOUR;
  closeStr = closeStr || WORK_END_HOUR;

  const tempSlots: TimeRange[] = [];
  let currentDay = workStartDt.startOf('day');

  while (currentDay.isBefore(workEndDt) || currentDay.isSame(workEndDt, 'day')) {
    const dateStr = currentDay.format('YYYY-MM-DD');
    const openDt = dayjs(`${dateStr} ${openStr}`);
    const originCloseDt = dayjs(`${dateStr} ${closeStr}`);
    let closeDt = dayjs(`${dateStr} ${closeStr}`);

    // Check the closeDt is the next day
    const isOverNight = openDt.isAfter(closeDt);

    // Validate the created dayjs objects
    if (!openDt.isValid() || !closeDt.isValid()) {
      currentDay = currentDay.add(1, 'day');
      continue;
    }

    if (isOverNight) {
      closeDt = closeDt.add(1, 'day');
    }

    // before open time 1 hour
    addIfOverlap(
      currentDay.startOf('day'), // start
      // new logic: remove 1 hour before open time
      openDt.subtract(0, 'hour'), // end
      workStartDt, // rangeStart
      workEndDt, // rangeEnd
      tempSlots, // results
      isOverNight ? originCloseDt : openDt, // If over night, use originCloseDt, otherwise use openDt
      isOverNight,
    );

    // after close time 30 minutes
    addIfOverlap(
      closeDt, // start
      currentDay.endOf('day').add(1, 'second'), // end
      workStartDt, // rangeStart
      workEndDt, // rangeEnd
      tempSlots, // results
      isOverNight ? originCloseDt : openDt, // If over night, use originCloseDt, otherwise use openDt
      isOverNight,
    );

    currentDay = currentDay.add(1, 'day');
  }

  return mergeContinuousSlots(tempSlots, workEnd, closeStr);
}


export const addIfOverlap = (
  start: dayjs.Dayjs | string | null | undefined,
  end: dayjs.Dayjs | string | null | undefined,
  rangeStart: dayjs.Dayjs | string | null | undefined,
  rangeEnd: dayjs.Dayjs | string | null | undefined,
  results: TimeRange[],
  timeStore: dayjs.Dayjs | string | null | undefined,
  isOverNight: boolean,
): void => {
  // Validate inputs
  if (!start || !end || !rangeStart || !rangeEnd) {
    return;
  }

  // Convert to dayjs objects and validate
  const startDt = dayjs(start);
  const timeStoreDt = dayjs(timeStore);
  const endDt = dayjs(end);
  const rangeStartDt = dayjs(rangeStart);
  const rangeEndDt = dayjs(rangeEnd);

  // Check if all dayjs objects are valid
  if (!startDt.isValid() || !endDt.isValid() || !rangeStartDt.isValid() || !rangeEndDt.isValid()) {
    return;
  }

  // Find the overlap
  // Get the max of startDt, timeStoreDt, rangeStartDt
  const s = isOverNight ? dayjs.max(startDt, timeStoreDt, rangeStartDt) : dayjs.max(startDt, rangeStartDt);
  // Get the min of endDt, rangeEndDt
  const e = dayjs.min(endDt, rangeEndDt);
  
  // Check if there's a valid overlap (start is before end)
  if (s.isValid() && e.isValid() && s.isBefore(e)) {
    results.push({
      start_date: s.format(FORMAT_TIME),
      end_date: e.format(FORMAT_TIME)
    });
  }
}


export const mergeContinuousSlots = (slots: TimeRange[], workEnd: string, closeStr: string): TimeRange[] => {
  // Validate and sort slots
  const validSlots = slots.filter(slot => {
    const startDt = dayjs(slot.start_date, FORMAT_TIME);
    const endDt = dayjs(slot.end_date, FORMAT_TIME);
    return startDt.isValid() && endDt.isValid();
  });

  if (validSlots.length < 1) return validSlots;

  validSlots.sort((a: TimeRange, b: TimeRange) => dayjs(a.start_date, FORMAT_TIME).valueOf() - dayjs(b.start_date, FORMAT_TIME).valueOf());

  const merged = [];
  let prev = validSlots[0];

  for (let i = 1; i < validSlots.length; i+=1) {
    const curr = validSlots[i];
    if (prev.end_date === curr.start_date) {
      // Merge range
      prev = { start_date: prev.start_date, end_date: curr.end_date };
    } else {
      merged.push(prev);
      prev = curr;
    }
  }
  merged.push(prev);

  // Validate workEnd and closeStr before processing
  if (!workEnd || !closeStr) {
    return merged;
  }

  const workEndDt = dayjs(workEnd, FORMAT_TIME);
  if (!workEndDt.isValid()) {
    return merged;
  }

  const [closeH, closeM] = closeStr.split(':').map(Number);
  if (isNaN(closeH) || isNaN(closeM)) {
    return merged;
  }

  const currentDay = workEndDt.startOf('day');
  const closeDt = currentDay.hour(closeH).minute(closeM).second(0);

  const diff = workEndDt.diff(closeDt, 'minute');
  if (diff > 0 && diff <= EVENING_START_OFFSET_MINUTES) {
    merged.pop();
  }

  return merged;
}


export const checkTimeListInWorkingHours = (
  taskTimeList: TimeRange[],
  openingTime: string = WORK_START_HOUR,
  closingTime: string = WORK_END_HOUR,
): boolean => {
  if (!taskTimeList) {
    return false;
  }

  openingTime = openingTime || WORK_START_HOUR;
  closingTime = closingTime || WORK_END_HOUR;

  const isWithinWorking = taskTimeList.some((time: TimeRange) => {
    const shifts = generateTimeSlots(time.start_date, time.end_date, openingTime, closingTime);
    return shifts.length > 0;
  });
  return isWithinWorking
}

// Function to generate affiliate code
export const generateAffiliateCode = (storeCode: string, format: string = "YYYYMMDDHHmmss"): string => {
  storeCode = storeCode || "000";
  const now = dayjs();
  const timestamp = now.format(format);
  return `P-${storeCode}-${timestamp}`;
};

export const getIdTokenFromLocalStorage = (): TypeCommon => {
  for (let i = 0; i < localStorage.length; i+=1) {
    const key = localStorage.key(i);
    if (key && key.includes(LOCAL_STORAGE_KEY.ID_TOKEN)) {
      return localStorage.getItem(key);
    }
  }
  return null;
};